import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableHead,
  Table,
  TableRow,
  TablePagination,
  Typography,
  TableCell,
  TableBody,
  Skeleton,
  IconButton,
  TableContainer,
  TextField,
  Tooltip,
  MenuItem,
  Fab,
  Chip,
} from '@mui/material'
import { ComplianceStatus, quoteLineType } from '../../../types'
import '../../lines/LinesShared.css'
import './ValidateLines.css'
import {
  ExpandLess,
  ExpandMore,
  CheckCircle,
  CheckCircleOutlined,
  Pending,
  PendingOutlined,
  Cancel,
  CancelOutlined,
  RateReviewOutlined,
  Refresh,
} from '@mui/icons-material'
import { BundleLineItem } from '../../lines/lineItemPricing'
import { formatCurrency } from 'src/helper'
import { isLastChildInBundle, toFlatRenderItems } from '../../lines/lineTableUtils'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { ProductCategoryCell } from '../../lines/ProductCategoryCell'
import { useNavigate } from 'react-router-dom'
import { useMemo, useState, type ReactNode } from 'react'
import { useDrawerState } from 'src/components/shared/useDrawerState'
import axios from 'axios'

const COLUMN_WIDTHS = {
  NUMBER: '80px',
  MFR_PART_NUMBER: '150px',
  DESCRIPTION: '350px',
  SCRM: '80px',
  DISTRIBUTOR: '130px',
  MANUFACTURER: '110px',
  PRODUCT_CATEGORY: '200px',
  CONTRACT_PRICE: '130px',
  TAA_COMPLIANT: '60px',
  CONTRACT_CATALOG: '130px',
  COMPLIANCE: '105px',
  UNIT_COST: '120px',
  EXT_COST: '130px',
  QTY: '80px',
  MARGIN: '135px',
  EXT_PRICE: '130px',
}

function handleFabClick() {
  console.log('handleFabClick')
}

function complianceIcon(compliance: ComplianceStatus | null, outlined = false): ReactNode {
  const Icons = outlined
    ? { Compliant: CheckCircleOutlined, PendingApproval: PendingOutlined, NotCompliant: CancelOutlined }
    : { Compliant: CheckCircle, PendingApproval: Pending, NotCompliant: Cancel }
  switch (compliance) {
    case ComplianceStatus.Compliant:
      return (
        <Tooltip
          className="compliance-tooltip"
          placement="left"
          arrow
          title={<Box className="compliance-tooltip-content">{['Compliant']}</Box>}
        >
          <Icons.Compliant color="success" />
        </Tooltip>
      )
    case ComplianceStatus.PendingApproval:
      return (
        <Tooltip
          className="compliance-tooltip"
          placement="left"
          arrow
          title={<Box className="compliance-tooltip-content">{['Pending']}</Box>}
        >
          <Icons.PendingApproval color="warning" />
        </Tooltip>
      )
    case ComplianceStatus.NotCompliant:
      return (
        <Tooltip
          className="compliance-tooltip"
          placement="left"
          arrow
          title={<Box className="compliance-tooltip-content">{['Not Compliant']}</Box>}
        >
          <Icons.NotCompliant color="error" />
        </Tooltip>
      )
    default:
      return '-'
  }
}

function countActualLineItems(items: BundleLineItem[]) {
  return (
    items.flatMap(bundle => bundle.children ?? []).filter(item => item.LineNumber !== -1).length +
    items.filter(item => item.LineNumber !== -1).length
  )
}

function filterLineItemsByCompliance(items: BundleLineItem[], compliance: string) {
  return items.reduce<BundleLineItem[]>((acc, item) => {
    if (item.LineNumber === -1) {
      const children = (item.children ?? []).filter(child => child.Compliance === compliance)
      if (children.length > 0) acc.push({ ...item, children })
    } else if (item.Compliance === compliance) {
      acc.push({ ...item, children: [] })
    }
    return acc
  }, [])
}

function paginateLineItems(items: BundleLineItem[], page: number, rowsPerPage: number) {
  type FlatItem = {
    item: quoteLineType
    bundleHeader?: BundleLineItem
    bundleId: string | null
  }
  const flatItems: FlatItem[] = items.flatMap(bundle =>
    bundle.LineNumber === -1
      ? (bundle.children ?? []).map<FlatItem>(child => ({
          item: child,
          bundleHeader: bundle,
          bundleId: bundle.Bundle || null,
        }))
      : ([{ item: bundle, bundleId: null }] as FlatItem[]),
  )

  const pageItems = flatItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const bundleGroups = new Map<string, { header: BundleLineItem; items: quoteLineType[] }>()
  const result: BundleLineItem[] = []
  const seenBundles = new Set<string>()

  for (const { item, bundleHeader, bundleId } of pageItems) {
    if (bundleId && bundleHeader) {
      if (!bundleGroups.has(bundleId)) {
        bundleGroups.set(bundleId, { header: bundleHeader, items: [] })
      }
      bundleGroups.get(bundleId)?.items.push(item)
    }
  }

  for (const { item, bundleHeader, bundleId } of pageItems) {
    if (bundleId && bundleHeader) {
      if (!seenBundles.has(bundleId)) {
        seenBundles.add(bundleId)
        result.push({ ...bundleHeader, children: bundleGroups.get(bundleId)?.items ?? [] })
      }
    } else {
      result.push({ ...item, children: [] })
    }
  }

  return result
}

function ValidateLines() {
  const navigate = useNavigate()
  const {
    lineItems,
    lineItemsLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    collapsedBundles,
    setCollapsedBundles,
    lineCount,
    quoteId,
    sortedLineItems,
    quote,
    handleReloadLineItems,
  } = useQuoteLines()

  const reviewRequestDrawer = useDrawerState()
  const [reviewMessage, setReviewMessage] = useState('')
  const [reviewRequestLoading, setReviewRequestLoading] = useState(false)

  const handleReviewRequest = () => {
    setReviewRequestLoading(true)
    axios
      .post(`/quote/${quoteId}/requestComplianceReview`, { message: reviewMessage })
      .then(() => {
        setReviewMessage('')
        reviewRequestDrawer.close()
      })
      .catch(err => console.error('Failed to request compliance review', err))
      .finally(() => setReviewRequestLoading(false))
  }

  const [complianceFilter, setComplianceFilter] = useState<ComplianceStatus | 'all'>('all')

  const filteredSourceItems = useMemo(() => {
    if (complianceFilter === 'all') return null
    return filterLineItemsByCompliance(lineItems as BundleLineItem[], complianceFilter)
  }, [lineItems, complianceFilter])

  const displayItems = useMemo(() => {
    if (complianceFilter === 'all') return sortedLineItems
    return paginateLineItems(filteredSourceItems ?? [], page, rowsPerPage)
  }, [complianceFilter, sortedLineItems, filteredSourceItems, page, rowsPerPage])

  const isTRVisible = useMemo(
    () => quote.ContractVehicle?.includes('SEWP') || quote.ContractVehicle === 'ITES-4H',
    [quote],
  )

  const displayLineCount = complianceFilter === 'all' ? lineCount : countActualLineItems(filteredSourceItems ?? [])

  const flatRenderItems = useMemo(() => toFlatRenderItems(displayItems), [displayItems])

  return (
    <div className="lines-view validate-lines">
      <div className="lines-header-buttons">
        <div className="lines-header-buttons-left">
          <TextField
            select
            label="Compliance Filter"
            size="small"
            value={complianceFilter}
            onChange={e => {
              setComplianceFilter(e.target.value as ComplianceStatus | 'all')
              setPage(0)
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value={ComplianceStatus.Compliant}>Compliant</MenuItem>
            <MenuItem value={ComplianceStatus.PendingApproval}>Pending Approval</MenuItem>
            <MenuItem value={ComplianceStatus.NotCompliant}>Not Compliant</MenuItem>
          </TextField>
          {isTRVisible && (
            <Button variant="outlined" onClick={() => navigate(`/quote/${quoteId}/technicalrequest`)}>
              {quote.ContractVehicle === 'ITES-4H' ? 'Approval Request' : 'Technical Request'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => handleReloadLineItems()}
            disabled={lineItemsLoading}
          >
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<RateReviewOutlined />} onClick={reviewRequestDrawer.open}>
            Request Compliance Review
          </Button>
          {quote.Status === 'Pending Approval' && (
            <Chip label="Pending Approval" color="warning" icon={<PendingOutlined />} />
          )}
        </div>
        <div className="lines-header-buttons-right">
          <TablePagination
            count={displayLineCount}
            onPageChange={(_, newPage) => {
              setPage(newPage)
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value))
              setPage(0)
            }}
            component="div"
          />
        </div>
      </div>
      <TableContainer className="lines-table-container">
        <Table
          stickyHeader
          sx={{ minWidth: `${Object.values(COLUMN_WIDTHS).reduce((acc, curr) => acc + parseInt(curr), 0)}px` }}
        >
          <TableHead>
            <TableRow>
              <TableCell className="lines-sticky-col" width={COLUMN_WIDTHS.NUMBER}>
                <div className="lines-sticky-col-content">
                  #
                  <IconButton
                    size="small"
                    onClick={() =>
                      setCollapsedBundles(prev => {
                        const newSet = new Set(prev)
                        newSet.size === lineItems.length
                          ? newSet.clear()
                          : lineItems.forEach(item => newSet.add(item.Bundle ?? 'no-bundle'))
                        return newSet
                      })
                    }
                  >
                    {collapsedBundles.size === lineItems.length ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </div>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.MFR_PART_NUMBER}>Mfr Part #</TableCell>
              <TableCell sx={{ minWidth: COLUMN_WIDTHS.DESCRIPTION }}>Description</TableCell>
              <TableCell align="center" width={COLUMN_WIDTHS.SCRM}>
                SCRM
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.DISTRIBUTOR}>Supplier</TableCell>
              <TableCell width={COLUMN_WIDTHS.MANUFACTURER}>Manufacturer</TableCell>
              <TableCell width={COLUMN_WIDTHS.PRODUCT_CATEGORY}>Product Category</TableCell>
              <TableCell width={COLUMN_WIDTHS.TAA_COMPLIANT}>TAA</TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.CONTRACT_PRICE}>
                Contract Ceiling Price
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.CONTRACT_CATALOG}>Contract Catalog</TableCell>
              <TableCell align="center" width={COLUMN_WIDTHS.COMPLIANCE}>
                Compliance
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.QTY}>
                Qty
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.UNIT_COST}>
                Unit Cost
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.EXT_COST}>
                Unit Price
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.EXT_PRICE}>
                Ext Price
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.MARGIN}>
                Gross Profit (%)
              </TableCell>
            </TableRow>
          </TableHead>
          {lineItemsLoading ? (
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 16 }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              {flatRenderItems.map(renderItem => {
                const lastChild = isLastChildInBundle(lineItems, renderItem)

                if (renderItem.isHeader) {
                  return (
                    <TableRow key={renderItem.item.LineItemId} className="bundle-row">
                      <TableCell className="lines-sticky-col">
                        <div className="lines-sticky-col-content">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setCollapsedBundles(prev => {
                                const newSet = new Set(prev)
                                newSet.has(renderItem.item.Bundle ?? 'no-bundle')
                                  ? newSet.delete(renderItem.item.Bundle ?? 'no-bundle')
                                  : newSet.add(renderItem.item.Bundle ?? 'no-bundle')
                                return newSet
                              })
                            }
                          >
                            {collapsedBundles.has(renderItem.item.Bundle ?? 'no-bundle') ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        </div>
                      </TableCell>
                      <TableCell className="lines-ellipsis-col">
                        {renderItem.item.MfrPartNumber ?? 'No Bundle'}
                      </TableCell>
                      <TableCell className="lines-ellipsis-col">{renderItem.item.Description}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell>{renderItem.item.TAA ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">{formatCurrency(renderItem.item.ContractCeilingPrice, '-')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right" className="bundle-small-text">
                        {renderItem.item.Qty ?? '-'}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.UnitCost, '-')}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.UnitPrice, '-')}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.ExtPrice, '-')}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {renderItem.item.Margin != null ? `${(renderItem.item.Margin * 100).toFixed(2)}%` : '-'}
                      </TableCell>
                    </TableRow>
                  )
                }

                return (
                  <TableRow
                    key={renderItem.item.LineItemId}
                    className={renderItem.bundleId === null ? 'no-bundle' : lastChild ? 'child-row-last' : undefined}
                    sx={{
                      display: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle') ? 'none' : 'table-row',
                    }}
                  >
                    <TableCell className="lines-sticky-col">
                      <Typography variant="body2">
                        {renderItem.item.LineNumber !== -1 ? renderItem.item.LineNumber : ''}
                      </Typography>
                    </TableCell>
                    <TableCell className="lines-ellipsis-col">{renderItem.item.MfrPartNumber ?? '-'}</TableCell>
                    <TableCell className="lines-ellipsis-col">{renderItem.item.Description ?? '-'}</TableCell>
                    <TableCell align="center" className="compliance-icon">
                      {complianceIcon(renderItem.item.Scrm, true)}
                    </TableCell>
                    <TableCell>{renderItem.item.Provider ?? '-'}</TableCell>
                    <TableCell className="lines-ellipsis-col">{renderItem.item.Manufacturer ?? '-'}</TableCell>
                    <TableCell>
                      <ProductCategoryCell item={renderItem.item} />
                    </TableCell>
                    <TableCell>{renderItem.item.TAA ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.ContractCeilingPrice, '-')}</TableCell>
                    <TableCell>{renderItem.item.ContractCatalog ?? '-'}</TableCell>
                    <TableCell align="center" className="compliance-icon">
                      {complianceIcon(renderItem.item.Compliance)}
                    </TableCell>
                    <TableCell align="right">{renderItem.item.Qty ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.UnitCost, '-')}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.UnitPrice, '-')}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.ExtPrice, '-')}</TableCell>
                    <TableCell align="right">
                      {renderItem.item.Margin != null ? `${(renderItem.item.Margin * 100).toFixed(2)}%` : '-'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <Fab
        variant="extended"
        color="primary"
        onClick={handleFabClick}
        disabled={lineItemsLoading || quote.Status === 'Pending Approval'}
        className="export-quote-fab"
      >
        {quote.Status === 'Validate' ? 'Send for Approval' : 'Export Quote'}
      </Fab>
      <Dialog open={reviewRequestDrawer.isOpen} onClose={reviewRequestDrawer.close} fullWidth maxWidth="sm">
        <DialogTitle>Request Compliance Review</DialogTitle>
        <DialogContent>
          <Typography marginBottom={3}>
            The compliance team will receive and review this help request. Please describe the issue in detail below.
          </Typography>
          <TextField
            label="Help Request"
            multiline
            minRows={6}
            value={reviewMessage}
            onChange={e => setReviewMessage(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={reviewRequestDrawer.close}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReviewRequest}
            loading={reviewRequestLoading}
            disabled={!reviewMessage.trim()}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ValidateLines
